import { StampComponents } from '@/components/Pages/home/components/StampComponents'
import { Typography } from '@/components/UI/Typography'
import type { CampaignSheetMode } from '@/schemas/v2/campaign'
import styles from '@/styles/Home.module.css'
import { IconLock } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

interface StampInSheet {
  id: number
  name: string
  spotId: number
  spots: Spot[]
  period: boolean
  point: number
  image: string
  overlayImage?: string | null
  hasStamped?: boolean
  isHas?: boolean
  hasCount?: number
  stampCount?: number
  earnedPoints?: number
}

interface UserSheetObject {
  id: number
  name: string
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  stamps: StampInSheet[]
}

interface Spot {
  address?: string
  id: number
  name: string
  zip?: string
}

export interface StampSheetStampInfo {
  id: number
  name: string
  image: string | null
  point: number
  isHas: boolean
  spots: Spot[]
}

interface StampSheetProps {
  sheet: UserSheetObject
  campaign: {
    isRepeatStamp: boolean
    stampShape: 0 | 1
    sheetMode: CampaignSheetMode
  }
  /** シートが未開放（startAt が未来）かどうか */
  isLocked?: boolean
  /** シートの開放日時（ISO8601）。isLocked=true のときに表示する */
  startAt?: string | null
  /** スタンプをタップした時に呼ばれる。ダイアログ表示に使用する */
  onStampClick: (stamp: StampSheetStampInfo) => void
  /**
   * OVERLAY の重ね絵画像が読み込まれた時に呼ばれる。
   * 重ね絵画像は非同期ロードのため、親の Swiper(autoHeight) は初期測定で高さを取りこぼし
   * 下部がクリップされる。ロード後に親が updateAutoHeight() で測り直すために使用する。
   */
  onOverlayImageLoad?: () => void
}

/**
 * campaign.type === 1の、通常のスタンプの場合のシート
 * */
export const StampSheet = ({
  sheet,
  campaign,
  isLocked = false,
  startAt,
  onStampClick,
  onOverlayImageLoad,
}: StampSheetProps) => {
  const { t } = useTranslation()
  if (campaign.sheetMode === 'NORMAL' || campaign.sheetMode === 'BACKGROUND') {
    // それぞれの場合に応じたstyleを設定
    const style =
      campaign.sheetMode === 'NORMAL'
        ? { backgroundColor: '#ffffff' }
        : campaign.sheetMode === 'BACKGROUND'
          ? {
              backgroundImage: `url("${sheet.backgroundImage}")`,
              backgroundRepeat: 'repeat-y',
              backgroundSize: '100% auto',
            }
          : {}
    return (
      <div
        key={`div-${sheet.id}`}
        className="mt-4 rounded-lg pt-6 border border-gray-200 relative overflow-hidden"
        style={style}
      >
        {/* ロックオーバーレイ */}
        {isLocked && (
          <div className="absolute inset-0 z-10 bg-black/40 flex flex-col items-center justify-center rounded-lg pointer-events-none">
            <IconLock size={48} className="text-white" aria-hidden="true" />
            {startAt && (
              <p className="mt-2 text-white text-xs font-bold text-center leading-relaxed">
                {new Date(startAt).toLocaleDateString('ja-JP', {
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                開放
              </p>
            )}
          </div>
        )}
        <div className="relative">
          <Typography variant="body1" component="h2" className="px-3 text-center">
            {sheet.name}
          </Typography>
        </div>
        <div className="mt-1 mr-3 flex items-center justify-end">
          <Typography variant="body2">
            {campaign.isRepeatStamp
              ? `${t('home.you_got', '獲得数')}： ${sheet.hasCount}${t('home.count_unit', '個')}`
              : `${t('home.achievement_count', '達成状況')}： ${sheet.hasCount} / ${sheet.total}`}
          </Typography>
        </div>
        <div className={`grid grid-cols-3 items-start justify-items-center ${styles.c_stamp_contents_wrap}`}>
          {sheet.stamps?.map((contentStamp, i: number) => {
            const stampShape: 'circular' | 'square' = campaign.stampShape === 1 ? 'square' : 'circular'
            return (
              <StampComponents
                key={`slide-${sheet.id}-stamp-cmp-${contentStamp.spotId}-${i}`}
                query={contentStamp.spotId}
                hasCount={contentStamp.hasCount ?? 0}
                course={sheet.name}
                img={contentStamp.image}
                name={contentStamp.name}
                spots={contentStamp.spots as [{ address: string; id: number; name: string; zip: string }]}
                isRepeatStamp={campaign?.isRepeatStamp}
                stampShape={stampShape}
                onGroupModal={() =>
                  onStampClick({
                    id: contentStamp.id,
                    name: contentStamp.name,
                    image: contentStamp.image,
                    point: contentStamp.point,
                    isHas: contentStamp.isHas ?? false,
                    spots: contentStamp.spots,
                  })
                }
              />
            )
          })}
        </div>
      </div>
    )
  }
  if (campaign.sheetMode === 'OVERLAY') {
    return (
      <div className="mt-4 rounded-lg border border-gray-200 relative overflow-hidden">
        {/*
          重ね絵：背景画像を幅いっぱい（w-full）に表示して画像全体を見せ、
          取得済み overlayImage を同じ領域に重ねる。シート名・達成状況・スタンプグリッドは表示しない（#3124）。
        */}
        <div className="relative w-full">
          {sheet.backgroundImage && (
            <img
              key={`img-1-${sheet.id}`}
              src={sheet.backgroundImage}
              alt={''}
              className="block w-full h-auto"
              onLoad={onOverlayImageLoad}
            />
          )}
          {sheet.stamps?.map((v) => {
            if ((v.hasCount ?? 0) < 1 || !v.overlayImage) return null
            return (
              <img
                key={v.name}
                src={v.overlayImage}
                alt={v.name}
                className="absolute inset-0 w-full h-full object-contain"
              />
            )
          })}
        </div>
      </div>
    )
  }
}
